'use client';

import React from 'react';
import Image from '@/components/Image';
import { EPlatform } from '@/utils/enums/platform-enum';
import { CommodityLookbook as LookbookType } from '../types';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Scrollbar } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/scrollbar';
import 'swiper/css/free-mode';
import Link from 'next/link';

interface CommodityLookbookProps {
  data: LookbookType;
  deviceType: EPlatform;
}

export default function CommodityLookbook({ data, deviceType }: CommodityLookbookProps) {
  const isWeb = deviceType === EPlatform.WEB;

  if (!data || !data.list || data.list.length < 4) return null;

  return (
    <div className="w-full pt-[20px] md:pt-[40px] bg-white overflow-hidden">
      <div className="max-w-[1920px] mx-auto pl-[3px] md:pl-[20px]">
        {data.title && (
          <h2 className="font-bb text-[22px] md:text-[32px] lg:text-[36px] text-[#282828] text-center mb-[20px] md:mb-[50px]">
            {data.title}
          </h2>
        )}

        <Swiper
          modules={[FreeMode, Scrollbar]}
          spaceBetween={isWeb ? 10 : 3}
          slidesPerView="auto"
          freeMode={true}
          scrollbar={{ draggable: true, hide: true }}
          className="lookbook-swiper !pb-[20px]"
        >
          {data.list.map((item, index) => {
            const imageResource = isWeb ? item.pc_image : item.wap_image;
            if (!imageResource) return null;

            return (
              <SwiperSlide key={index} className="!w-[174px] md:!w-[360px] lg:!w-[23.4375vw]">
                <div className="w-full group relative rounded-[8px] md:rounded-[12px] overflow-hidden bg-gray-100">
                  <div
                    className="w-full relative"
                    style={{
                      aspectRatio: imageResource.aspectRatio
                        ? imageResource.aspectRatio.replace('/', ' / ')
                        : isWeb
                          ? '450/600'
                          : '174/232',
                    }}
                  >
                    {item.shop_sku ? (
                      <Link href={`/goods-detail/${item.shop_sku}`} className="block w-full h-full">
                        <Image
                          src={imageResource.url}
                          alt={item.image_alt || item.influencer_name}
                          fill
                          className="object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </Link>
                    ) : (
                      <Image
                        src={imageResource.url}
                        alt={item.image_alt || item.influencer_name}
                        fill
                        className="object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    )}
                  </div>
                  {item.influencer_name && (
                    <div className="absolute bottom-[8px] right-[8px] md:bottom-[20px] md:right-[20px] bg-white/60 rounded-100 px-[6px] py-[2px] md:px-[8px] z-10 flex items-center justify-center pointer-events-none">
                      <span className="font-br text-[10px] md:text-[12px] text-[#282828] leading-[1] md:leading-[1.2]">
                        @{item.influencer_name.replace(/^@/, '')}
                      </span>
                    </div>
                  )}
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </div>
  );
}
